import React, { useRef } from 'react';
import { propsToDataAttributes } from '@tr-digital/react-rehydrate';
import './index.css';
import {
  Button,
  Dropdown,
  FlexGrid,
  getClassNameFactory,
  getRehydratableName,
  Heading,
  InlineErrorMessage,
  Rank,
  Responsive,
  Typography,
} from '@dcl/ui';
import { PayRollTabs } from './component/PayRollTabs';
import { IPayrollTax } from './types';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography';
import { types } from '@dcl/ui/dist/elements/Heading';
import {
  constructStateUrl,
  constructUltraTaxStateUrl,
  filterFn,
  filterProduct,
} from './helper';
import {
  ACCOUNTINGCS,
  CLEAR_BUTTON_LABEL,
  DESKTOP_MIN_WIDTH,
  federalState,
  MOBILE_MAX_WIDTH,
} from './constants';
import { ButtonColor, ButtonKind } from '@dcl/ui/dist/elements/Button';
import ReactHtmlParser from 'react-html-parser';

export const PayrollTax: React.FC<IPayrollTax> = (props) => {
  const {
    payrollType,
    title,
    description,
    stateDropdownLabel,
    stateDropdown,
    buttonLabelDesktop,
    buttonLabelMobile,
    stateDefaultSelectionLabel,
    statePath,
    localityButtonLabel,
    localityDropdownLabel,
    localityDefaultSelectionLabel,
    resourcePath,
  } = props;
  const getClassName = getClassNameFactory(PayrollTax.displayName);
  const [selectedState, setSelectedState] = React.useState('');
  const [showError, setShowError] = React.useState(false);
  const [stateData, setStateData] = React.useState<any>([]);
  const [selectedTab, setSelectedTab] = React.useState('');
  const [localformSelected, setLocalformSelected] = React.useState('');
  const dropdownRef = useRef<Dropdown>();

  const dropdownItems = [...stateDropdown]
    .sort((itemA, itemB) => {
      if (itemA.title.toLowerCase() === federalState) return -1;
      if (itemB.title.toLowerCase() === federalState) return 1;
      const labelA = itemA.title.toLowerCase();
      const labelB = itemB.title.toLowerCase();
      if (labelA < labelB) return -1;
      if (labelA > labelB) return 1;
      return 0;
    })
    .map((item) => ({
      label: capitalizeFirstLetter(item.title),
      value: item.value,
    }));

  function capitalizeFirstLetter(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  const handleClearButtonClick = () => {
    setSelectedState('');
    setStateData([]);

    const urlWithParams = new URL(window.location.href);
    urlWithParams.searchParams.delete('hs_state');
    urlWithParams.searchParams.delete('hs_form');
    urlWithParams.searchParams.delete('hs_localforms');
    setSelectedTab('tab1');
    setLocalformSelected('');
    window.history.pushState(null, '', urlWithParams.toString());
  };

  const handleValueChange = (value: string) => {
    setSelectedState(filterProduct(value, stateDropdown));
    setShowError(false);
    const urlWithParams = new URL(window.location.href);
    urlWithParams.searchParams.delete('hs_state');
    urlWithParams.searchParams.delete('hs_form');
    urlWithParams.searchParams.delete('hs_localforms');
    window.history.pushState(null, '', urlWithParams.toString());
  };

  const handleStateApi = () => {
    if (selectedState === '') {
      setShowError(true);
      return;
    }
    const url =
      payrollType === ACCOUNTINGCS
        ? constructStateUrl(statePath, selectedState)
        : constructUltraTaxStateUrl(resourcePath, selectedState);

    const urlWithParams = new URL(window.location.href);
    if (urlWithParams.searchParams.has('hs_state')) {
      urlWithParams.searchParams.set('hs_state', selectedState);
    } else {
      urlWithParams.searchParams.append('hs_state', selectedState);
    }
    setSelectedTab('tab1');
    window.history.pushState(null, '', urlWithParams.toString());
    fetch(url)
      .then((response) => response.json())
      .then((data) => setStateData(data));
  };

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stateParam = urlParams.get('hs_state');
    const tabParam = urlParams.get('hs_form');
    const localFormsParam = urlParams.get('hs_localforms');
    if (stateParam) {
      const selectedDropdownItem = dropdownItems.find(
        (item) => item.value === stateParam
      );
      const selectedLabel = selectedDropdownItem
        ? selectedDropdownItem.label
        : '';

      dropdownRef?.current?.setState({ value: selectedLabel });
      setSelectedState(stateParam);
      setShowError(false);
      const url =
        payrollType === ACCOUNTINGCS
          ? constructStateUrl(statePath, stateParam)
          : constructUltraTaxStateUrl(resourcePath, stateParam);
      fetch(url)
        .then((response) => response.json())
        .then((data) => {
          if (tabParam) {
            const tabIndex = data?.tabs?.findIndex(
              (tab) => tab.tabTitle === tabParam
            );
            tabIndex !== -1
              ? setSelectedTab(`tab${tabIndex + 1}`)
              : setSelectedTab('tab1');
          } else {
            setSelectedTab('tab1');
          }

          localFormsParam && setLocalformSelected(localFormsParam);

          setStateData(data);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-rehydratable={getRehydratableName(PayrollTax.displayName)}
      className={getClassName()}
      {...propsToDataAttributes(props)}
    >
      <FlexGrid>
        <FlexGrid.Row>
          <FlexGrid.Col xs={12} lg={12} md={12}>
            <Heading
              rank={Rank.First}
              className={getClassName({ descendantName: 'mainHeading' })}
              type={types.Large}
            >
              {title}
            </Heading>
          </FlexGrid.Col>
          <FlexGrid.Col
            xs={12}
            lg={12}
            md={12}
            className={getClassName({ descendantName: 'descriptionDiv' })}
          >
            <Typography
              tag={TypographyTags.P}
              className={getClassName({ descendantName: 'description' })}
            >
              {ReactHtmlParser(description)}
            </Typography>
          </FlexGrid.Col>
          <FlexGrid.Col
            xs={12}
            lg={12}
            md={12}
            className={getClassName({ descendantName: 'searchDropdownDiv' })}
          >
            <FlexGrid.Row>
              <FlexGrid.Col xs={12} lg={12} md={12}>
                <label
                  className={getClassName({
                    descendantName: 'searchDropdownLabel',
                  })}
                >
                  {stateDropdownLabel}
                </label>
              </FlexGrid.Col>
              <FlexGrid.Col
                xs={12}
                lg={4}
                md={4}
                className={getClassName({ descendantName: 'searchDropdown' })}
              >
                <Dropdown
                  ref={(el) => (dropdownRef.current = el!)}
                  filterFunction={filterFn}
                  typeaheadMode
                  clearButtonLabel={CLEAR_BUTTON_LABEL}
                  onClearButtonClick={handleClearButtonClick}
                  maxVisibleItems={5}
                  block={true}
                  items={dropdownItems}
                  labeledBy={stateDropdownLabel}
                  placeholder={
                    payrollType === ACCOUNTINGCS
                      ? ' '
                      : stateDefaultSelectionLabel
                  }
                  onValueChanged={(value) => handleValueChange(value as string)}
                  className={getClassName({
                    descendantName: 'customDropdownStyle',
                  })}
                  removeAutoFocus
                />
                {showError && (
                  <InlineErrorMessage
                    id="validationError"
                    errorMessage={stateDropdownLabel}
                    className={getClassName({
                      descendantName: 'error',
                      modifiers: showError ? 'visible' : 'hidden',
                    })}
                  />
                )}
              </FlexGrid.Col>

              <FlexGrid.Col
                xs={12}
                lg={8}
                md={12}
                className={`${getClassName({
                  descendantName: 'searchStateButtonDiv',
                })} ${
                  showError
                    ? getClassName({
                        descendantName: 'searchStateButtonDivError',
                      })
                    : ''
                }`}
              >
                <Responsive minWidth={DESKTOP_MIN_WIDTH}>
                  <Button
                    kind={ButtonKind.BUTTON}
                    color={ButtonColor.PRIMARY}
                    disabled={false}
                    data-testid="submitStateButton"
                    onClick={handleStateApi}
                  >
                    {buttonLabelDesktop}
                  </Button>
                </Responsive>
                <Responsive minWidth={0} maxWidth={MOBILE_MAX_WIDTH}>
                  <Button
                    kind={ButtonKind.BUTTON}
                    color={ButtonColor.PRIMARY}
                    disabled={false}
                    data-testid="submitButton"
                    onClick={handleStateApi}
                    block={true}
                  >
                    {buttonLabelMobile}
                  </Button>
                </Responsive>
              </FlexGrid.Col>
            </FlexGrid.Row>
          </FlexGrid.Col>

          {stateData.tabs && stateData.tabs.length > 0 && (
            <FlexGrid.Col
              xs={12}
              lg={12}
              md={12}
              className={getClassName({ descendantName: 'payrollData' })}
            >
              <PayRollTabs
                stateData={stateData}
                localityButtonLabel={localityButtonLabel}
                localityDropdownLabel={localityDropdownLabel}
                payrollType={payrollType}
                localityDefaultSelectionLabel={localityDefaultSelectionLabel}
                selectedTab={selectedTab}
                selectLocalForm={localformSelected}
              />
            </FlexGrid.Col>
          )}
        </FlexGrid.Row>
      </FlexGrid>
    </div>
  );
};

PayrollTax.displayName = 'PayrollTax';
